import { Typography, MenuItem, Menu, Stack } from '@mui/material';
import React from 'react';
import { useDropdownState } from '../../../hooks';
import clsx from 'clsx';
import './AssigneeFilter.scss';
import { Check, PersonOutline, ExpandMore } from '@mui/icons-material';
import { useRecoilState } from 'recoil';
import { taskFilterAtom } from '../../../data-access/recoil/todo.atoms';
import { useTranslation } from 'react-i18next';

const ASSIGNEE_OPTIONS = [
  { value: 'include-all', labelKey: 'task.filters.includeAll', onlyMyTask: false },
  { value: 'assigned-to-me', labelKey: 'task.filters.assignedToMe', onlyMyTask: true },
] as const;

const AssigneeFilter = () => {
  const { t } = useTranslation();
  const { anchorEl, handleOpenDropdown, handleCloseDropdown } = useDropdownState();
  const [taskFilter, setTaskFilter] = useRecoilState(taskFilterAtom);
  const selectedAssignee = taskFilter.onlyMyTask ? 'assigned-to-me' : 'include-all';

  const handleAssigneeSelect = (assignee: (typeof ASSIGNEE_OPTIONS)[number]) => {
    setTaskFilter((prev) => ({
      ...prev,
      onlyMyTask: assignee.onlyMyTask,
    }));
    handleCloseDropdown();
  };

  const renderMenuItem = (option: (typeof ASSIGNEE_OPTIONS)[number]) => (
    <MenuItem
      key={option.value}
      className={clsx('assignee-selector__dropdown__item', {
        'assignee-selector__dropdown__item--selected': selectedAssignee === option.value,
      })}
      onClick={() => handleAssigneeSelect(option)}
    >
      <Typography variant="body2" className="assignee-selector__dropdown__item__text">
        {t(option.labelKey)}
      </Typography>
      {selectedAssignee === option.value && (
        <Check className="assignee-selector__dropdown__item__check" />
      )}
    </MenuItem>
  );

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDropdown(event as any);
    }
  };

  return (
    <>
      <Stack
        className={clsx(
          'assignee-selector',
          anchorEl && 'assignee-selector--open',
          taskFilter.onlyMyTask && 'assignee-selector--selected'
        )}
        onClick={handleOpenDropdown}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        role="button"
        aria-label={t('task.filters.assigneeFilter')}
        tabIndex={0}
        onKeyDown={handleTriggerKeyDown}
      >
        <PersonOutline className="assignee-selector__icon" />
        <ExpandMore className="assignee-selector__arrow" />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseDropdown}
        className="assignee-selector__dropdown"
      >
        <div className="assignee-selector__dropdown__header">
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="body2" className="assignee-selector__dropdown__header__text">
              {t('task.filters.filterByAssignee')}
            </Typography>
            <span className="connect-todos-dropdown__beta">{t('task.todoIntegration.beta')}</span>
          </Stack>
          <Typography variant="caption">{t('task.filters.assigneeGithubOnly')}</Typography>
        </div>
        {ASSIGNEE_OPTIONS.map(renderMenuItem)}
      </Menu>
    </>
  );
};

export default AssigneeFilter;
